import { Injectable, inject, signal } from '@angular/core';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import { DialogService, SnackbarService } from '@vectronic/util';
import { firstValueFrom } from 'rxjs';
import { ScheduleTemplatesProviderService } from './schedule-templates-provider.service';
import { ScheduleServices } from './schedule.service';

@Injectable({ providedIn: 'root' })
export class GpsXFileImportService {
  private readonly defaultSkipCountNotSupportedMessage =
    'Default skip count is not supported in INVENTA. Please confirm that default skip count will be transformed to skip count = 0.';

  private readonly gpsXFileExtensions: Partial<Record<dsEnums.ScheduleType, string>> = {
    GPS: '.vgsf',
    VHF: '.vbsf',
    CAMERA: '.visf',
    COMMUNICATION: '.vcsf',
    PROXIMITY: '.vgsf',
    VIRTUAL_FENCE: '.vgsf',
  };

  private readonly scheduleServices = inject(ScheduleServices);
  private readonly dialogService = inject(DialogService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly scheduleTemplatesProvider = inject(ScheduleTemplatesProviderService, {
    optional: true,
  });
  loading = signal(false);

  getAcceptedFileExtension(scheduleType?: dsEnums.ScheduleType): string | undefined {
    if (!this.isSupportedScheduleType(scheduleType)) {
      return undefined;
    }

    return this.gpsXFileExtensions[scheduleType];
  }

  canLoad(scheduleType?: dsEnums.ScheduleType, readonly = false): boolean {
    return Boolean(this.getAcceptedFileExtension(scheduleType)) && !readonly;
  }

  async importScheduleFromEvent(
    event: Event,
    scheduleType?: dsEnums.ScheduleType,
  ): Promise<boolean> {
    if (!this.isSupportedScheduleType(scheduleType)) {
      return false;
    }

    const inputElement = event.target as HTMLInputElement | null;
    const file = inputElement?.files?.[0];

    if (inputElement) {
      inputElement.value = '';
    }

    if (!file) {
      return false;
    }

    if (!this.scheduleTemplatesProvider) {
      this.snackbarService.error('GPS+X file import is not available.');
      return false;
    }

    let requestBody: string[];
    try {
      requestBody = this.createRequestBody(await file.text());
    } catch {
      this.snackbarService.error(`Unable to read ${file.name}.`);
      return false;
    }

    try {
      this.loading.set(true);
      const response = await this.loadScheduleFromXmlString(scheduleType, requestBody);

      if (!response) {
        return false;
      }

      const { schedule } = response;

      const isCamera = scheduleType === 'CAMERA';
      const rules = isCamera ? schedule?.cameraScheduleRules : schedule?.rules;

      if (!rules?.length) {
        const message = isCamera
          ? 'Imported camera schedule does not contain any rules.'
          : `Imported ${this.getScheduleLabel(scheduleType)} schedule does not contain any rules.`;
        this.snackbarService.error(message);
        return false;
      }

      this.scheduleServices[scheduleType].loadRules(rules);
      this.snackbarService.success(
        `Loaded ${this.getScheduleLabel(scheduleType)} schedule from ${file.name}.`,
      );
      return true;
    } catch {
      this.snackbarService.error(
        `Unable to load ${this.getScheduleLabel(scheduleType)} schedule from ${file.name}.`,
      );
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  private isSupportedScheduleType(
    scheduleType?: dsEnums.ScheduleType,
  ): scheduleType is dsEnums.ScheduleType {
    return Boolean(scheduleType && this.gpsXFileExtensions[scheduleType]);
  }

  private createRequestBody(fileContent: string): string[] {
    return fileContent
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  private async loadScheduleFromXmlString(
    scheduleType: dsEnums.ScheduleType,
    requestBody: string[],
    confirmDefaultSkipCountIsNotSupported = false,
  ): Promise<dsDtos.SingleSchedule | null> {
    try {
      return await firstValueFrom(
        this.scheduleTemplatesProvider.fromXmlString(
          scheduleType,
          requestBody,
          confirmDefaultSkipCountIsNotSupported,
        ),
      );
    } catch (error: any) {
      const errorMessage =
        typeof error === 'string' ? error : error?.error?.message || error?.message || '';
      if (
        !confirmDefaultSkipCountIsNotSupported &&
        errorMessage.includes(this.defaultSkipCountNotSupportedMessage)
      ) {
        const isConfirmed = await this.confirmDefaultSkipCountIsNotSupported();
        return isConfirmed ? this.loadScheduleFromXmlString(scheduleType, requestBody, true) : null;
      }

      throw error;
    }
  }

  private async confirmDefaultSkipCountIsNotSupported(): Promise<boolean> {
    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        title: 'Confirm Skip Count Transformation',
        message: this.defaultSkipCountNotSupportedMessage,
      },
    });

    return Boolean(await firstValueFrom(dialogRef.afterClosed()));
  }

  private getScheduleLabel(type: dsEnums.ScheduleType): string {
    switch (type) {
      case 'GPS':
        return 'GPS';
      case 'VHF':
        return 'Beacon';
      case 'COMMUNICATION':
        return 'Communication';
      case 'CAMERA':
        return 'Camera';
      case 'PROXIMITY':
        return 'Proximity';
      case 'VIRTUAL_FENCE':
        return 'Virtual Fence';
      default:
        return type;
    }
  }
}
